import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'class-names';
import moment from 'moment';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Loader from 'react-loader-spinner';
import { ChevronLeft } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';

import TextInput from '../../ui/TextInput/TextInput';
import PhoneInput from '../../PhoneInput/PhoneInput';
import BirthInput from '../../ui/BirthInput/BirthInput';

import * as AUTH_SELECTORS from '../../../store/selectors/auth';
import * as COMPANIES_SELECTORS from '../../../store/selectors/companies';

import * as AUTH_ACTIONS from '../../../store/actions/auth';

import { emptyFunc } from '../../../helpers/function/emptyFunc';

import { SIGN_UP_STEP } from '../../../const/auth/SIGN_UP_STEP';
import { LOCALE_NAMESPACE } from '../../../const/translations/LOCALE_NAMESPACE';

import styles from './SignUpForm.module.scss';

const T_PREFIX = 'signUp.form';
const T_FIELDS = `${T_PREFIX}.fields`;

const getTimerString = ({ countdown }) => {
    const minutes = moment
        .duration(countdown)
        .minutes()
        .toString()
        .padStart(2, '0');
    const seconds = moment
        .duration(countdown)
        .seconds()
        .toString()
        .padStart(2, '0');
    return `${minutes}:${seconds}`;
};

const Footer = () => {
    const { t } = useTranslation(LOCALE_NAMESPACE.COMMON);

    const dispatch = useDispatch();

    const {
        step,
        timer,
        isLoading,
        checkedEmail,
    } = useSelector(AUTH_SELECTORS.authSelector);

    const [hasTimeElapsed, setHasTimeElapsed] = useState(false);

    const handleResendEmail = useCallback(() => {
        dispatch(AUTH_ACTIONS.checkEmail(checkedEmail));
    }, [dispatch, checkedEmail]);

    useEffect(() => {
        if (!timer) {
            return emptyFunc;
        }
        setHasTimeElapsed(false);
        const delay = moment(timer).diff(moment(), 'milliseconds');
        const timeout = setTimeout(() => {
            setHasTimeElapsed(true);
        }, delay);
        return () => clearTimeout(timeout);
    }, [timer]);

    return (
        <div className={'d-flex flex-column justify-content-center align-items-center w-100'}>
            <Button
                form="sign-up-form"
                type="submit"
                variant="tint"
                size="lg"
                className={styles.button}
                disabled={isLoading || ([SIGN_UP_STEP.VERIFY_EMAIL].includes(step) && hasTimeElapsed)}
                data-testid="sign-up-button"
            >
                {isLoading ? (
                    <Loader
                        className="d-block text-center"
                        type="TailSpin"
                        color="#fff"
                        height={28}
                        width={28}
                    />
                ) : (
                    t(`signInScreen.${step || 'continue'}`)
                )}
            </Button>

            {[SIGN_UP_STEP.VERIFY_EMAIL].includes(step) && (
                <Button
                    type="button"
                    variant="light"
                    size="lg"
                    className={classNames(styles.button, styles.secondary)}
                    onClick={handleResendEmail}
                    disabled={isLoading || !hasTimeElapsed}
                >
                    {t('signInScreen.resendEmail')}
                </Button>
            )}
        </div>
    );
};

const MAX_LENGTH_FIRST_NAME = 24;
const MAX_LENGTH_LAST_NAME = 24;

const initialValues = {
    code: '',
    password: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    phoneNumber: '',
    countdown: '',
}

const SignUpForm = (props) => {
    const { showFooter } = props;

    const { t } = useTranslation(LOCALE_NAMESPACE.COMMON);
    const { t: ta } = useTranslation(LOCALE_NAMESPACE.AUTH);

    const dispatch = useDispatch();

    const {
        step,
        timer,
        checkedEmail,
        validationErrors,
        alreadyRegistered,
    } = useSelector(AUTH_SELECTORS.authSelector);
    const {
        name: companyName,
        license_link: licenseLink
    } = useSelector(COMPANIES_SELECTORS.selectedCompanyCompanySelector);

    const codeRef = useRef(null);
    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const birthDateRef = useRef(null);
    const phoneNumberRef = useRef(null);

    const validationSchema = yup.object({
        code: yup.string(),
        password: yup.string(),
        firstName: yup
            .string()
            .max(
                MAX_LENGTH_FIRST_NAME,
                t(
                    'errors.maxLengthWithName',
                    {
                        name: ta(`${T_FIELDS}.firstName.label`),
                        length: MAX_LENGTH_FIRST_NAME,
                    }
                ),
            ),
        lastName: yup
            .string()
            .max(
                MAX_LENGTH_LAST_NAME,
                t(
                    'errors.maxLengthWithName',
                    {
                        name: ta(`${T_FIELDS}.lastName.label`),
                        length: MAX_LENGTH_LAST_NAME,
                    }
                ),
            ),
        birthDate: yup.string(),
        phoneNumber: yup.string(),
        countdown: yup.string(),
    });

    const {
        values,
        errors,
        setFieldValue,
        handleSubmit,
    } = useFormik({
        validationSchema,
        initialValues,
        onSubmit: useCallback((values) => {
            switch (step) {
                case SIGN_UP_STEP.VERIFY_EMAIL: {
                    return dispatch(AUTH_ACTIONS.verifyEmail(values.code));
                }
                case SIGN_UP_STEP.FILL_PROFILE: {    
                    const contactNumber = values.phoneNumber.startsWith('+')
                        ? values.phoneNumber
                        : `+${values.phoneNumber}`;
                    return dispatch(AUTH_ACTIONS.fillProfile({
                        firstName: values.firstName,
                        lastName: values.lastName,
                        password: values.password,
                        birthDate: moment(values.birthDate, 'DD/MM/YYYY').toDate(),
                        contactNumber,
                    }));
                }
                default: {
                    return;
                }
            }
        }, [
            step,
            dispatch
        ])
    })

    const onHandleSubmit = useCallback((e) => {
        e.preventDefault();
        handleSubmit();
    }, [handleSubmit]);

    const handleCodeChange = useCallback((value) => {
        setFieldValue('code', value)
    }, [setFieldValue]);

    const handlePasswordChange = useCallback((value) => {
        setFieldValue('password', value)
    }, [setFieldValue]);

    const handleFirstNameChange = useCallback((value) => {
        setFieldValue('firstName', value)
    }, [setFieldValue]);

    const handleLastNameChange = useCallback((value) => {
        setFieldValue('lastName', value)
    }, [setFieldValue]);

    const handleBirthDateChange = useCallback((value) => {
        setFieldValue('birthDate', value)
    }, [setFieldValue]);

    const handlePhoneNumberChange = useCallback((value) => {
        setFieldValue('phoneNumber', value)
    }, [setFieldValue]);

    const handleScrollToRef = useCallback(({ ref }) => {
        window.scrollTo(0, ref.current.offsetTop);
    }, []);

    const handleScrollToCode = useCallback(() => {
        handleScrollToRef({ ref: codeRef });
    }, [handleScrollToRef]);

    const handleScrollToEmail = useCallback(() => {
        handleScrollToRef({ ref: emailRef });
    }, [handleScrollToRef]);

    const handleScrollToPassword = useCallback(() => {
        handleScrollToRef({ ref: passwordRef });
    }, [handleScrollToRef]);

    const handleScrollToFirstName = useCallback(() => {
        handleScrollToRef({ ref: firstNameRef });
    }, [handleScrollToRef]);

    const handleScrollToLastName = useCallback(() => {
        handleScrollToRef({ ref: lastNameRef });
    }, [handleScrollToRef]);

    const handleScrollToBirthDate = useCallback(() => {
        handleScrollToRef({ ref: birthDateRef });
    }, [handleScrollToRef]);

    const handleScrollToPhoneNumber = useCallback(() => {
        handleScrollToRef({ ref: phoneNumberRef });
    }, [handleScrollToRef]);

    const handleGoBack = useCallback(() => {
        dispatch(AUTH_ACTIONS.resetStep());
    }, [dispatch]);

    useEffect(() => {
        if (!timer) {
            return emptyFunc;
        }
        setFieldValue('countdown', moment(timer).diff(moment()).valueOf());
        const interval = setInterval(
            () => {
                setFieldValue('countdown', moment(timer).diff(moment()).valueOf());
            },
            1000,
        );
        return () => clearInterval(interval);
    // eslint-disable-next-line
    }, [timer]);

    useEffect(() => {
        if ([SIGN_UP_STEP.VERIFY_EMAIL].includes(step)) {
            codeRef.current?.focus?.();
        }
        if ([SIGN_UP_STEP.FILL_PROFILE].includes(step)) {
            firstNameRef.current?.focus?.();
        }
    }, [step]);

    return (
        <form
            id="sign-up-form"
            onSubmit={onHandleSubmit}
            className={styles.container}
        >
            <div className="w-100 mb-4">
                <button
                    type="button"
                    name="back"
                    className={classNames(
                        styles.backButton,
                        'bg-transparent border-0 px-0 ml-n2',
                    )}
                    onClick={handleGoBack}
                >
                    <ChevronLeft size={24} />
                    <span
                        className="small text-uppercase font-weight-bold"
                        style={{ position: 'relative', top: 1 }}
                    >
                        {t('signInScreen.back')}
                    </span>
                </button>
            </div>

            {[SIGN_UP_STEP.VERIFY_EMAIL].includes(step) && values.countdown > 0 && (
                <div className="d-flex flex-column align-items-center w-100 mb-2">
                    <Alert variant="warning">
                        <span>
                            {alreadyRegistered ? t("signInScreen.alreadyRegisteredNote") : t("signInScreen.pleaseWaitForEmail")}
                        </span>
                    </Alert>

                    <div
                        className="d-flex justify-content-center align-items-center rounded-circle border-tint"
                        style={{ width: 64, height: 64 }}
                    >
                        <span className="font-weight-bold">
                            {getTimerString({ countdown: values.countdown })}
                        </span>
                    </div>
                </div>
            )}

            <TextInput
                ref={emailRef}
                type="email"
                name={t('signInScreen.emailAddress')}
                value={checkedEmail}
                validationError={validationErrors?.email}
                onFocus={handleScrollToEmail}
                disabled
                data-testid="data-test-email"
            />

            {[SIGN_UP_STEP.VERIFY_EMAIL].includes(step) && (
                <TextInput
                    ref={codeRef}
                    type="number"
                    name={t('signInScreen.code')}
                    value={values.code}
                    setInputValue={handleCodeChange}
                    validationError={validationErrors?.code || errors.code}
                    className={styles.code}
                    onFocus={handleScrollToCode}
                />
            )}

            {[SIGN_UP_STEP.FILL_PROFILE].includes(step) && (
                <>
                    <TextInput
                        ref={firstNameRef}
                        name={ta(`${T_FIELDS}.firstName.label`)}
                        value={values.firstName}
                        setInputValue={handleFirstNameChange}
                        validationError={validationErrors?.firstName || errors.firstName}
                        onFocus={handleScrollToFirstName}
                    />
                    <TextInput
                        ref={lastNameRef}
                        name={ta(`${T_FIELDS}.lastName.label`)}
                        value={values.lastName}
                        setInputValue={handleLastNameChange}
                        validationError={validationErrors?.lastName || errors.lastName}
                        onFocus={handleScrollToLastName}
                    />

                    <PhoneInput
                        ref={phoneNumberRef}
                        value={values.phoneNumber}
                        onChange={handlePhoneNumberChange}
                        placeholder={t('signInScreen.phoneNumber')}
                        validationError={validationErrors?.contactNumber || errors.phoneNumber}
                        onFocus={handleScrollToPhoneNumber}
                    />

                    <BirthInput
                        ref={birthDateRef}
                        name={t('signInScreen.birthDate')}
                        value={values.birthDate}
                        placeholder="dd/mm/yyyy"
                        setInputValue={handleBirthDateChange}
                        validationError={validationErrors?.birthDate || errors.birthDate}
                        onFocus={handleScrollToBirthDate}
                    />

                    <TextInput
                        ref={passwordRef}
                        type="password"
                        name={t('signInScreen.password')}
                        value={values.password}
                        rules={{ minLength: 2 }}
                        setInputValue={handlePasswordChange}
                        validationError={validationErrors?.password || errors.password}
                        onFocus={handleScrollToPassword}
                    />

                    <p className={styles.privacy}>
                        <Trans
                            i18nKey={'common:signInScreen.privacy'}
                            components={[
                            // eslint-disable-next-line
                                <a href={licenseLink} target={'_blank'} className={'text-dark font-weight-bold'}/>, // eslint-disable-line jsx-a11y/anchor-has-content
                            // eslint-disable-next-line
                                <a href={'https://appybee.nl/legal/'} target={'_blank'} className={'text-dark'}/>, // eslint-disable-line jsx-a11y/anchor-has-content
                            // eslint-disable-next-line
                                <a href={'https://appybee.nl/privacy-verklaring/'} target={'_blank'} className={'text-dark'}/> // eslint-disable-line jsx-a11y/anchor-has-content
                        ]}
                            values={{ companyName }}
                        >
                            {/* eslint-disable-next-line */}
                            By continuing to the next step, you confirm that you agree to the terms of <span
                            // eslint-disable-next-line
                            className={'font-weight-bold'}>{companyName}</span> and the <a
                            // eslint-disable-next-line
                            href={'https://appybee.nl/legal/'} target={'_blank'} className={'text-dark'}>general
                            {/* eslint-disable-next-line */}
                            terms</a> and conditions and <a href={'https://appybee.nl/privacy-verklaring/'}
                            // eslint-disable-next-line
                                                            target={'_blank'} className={'text-dark'}>privacy
                            {/*  eslint-disable-next-line */}
                            statement</a> from AppyBee.
                        </Trans>
                    </p>
                </>
            )}

            {showFooter && (
                <div className="mt-3 w-100">
                    <Footer />
                </div>
            )}
        </form>
    );
};

SignUpForm.Footer = Footer;

SignUpForm.propTypes = {
    showFooter: PropTypes.bool,
};

SignUpForm.defaultProps = {
    showFooter: true,
};

export default SignUpForm;
